import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import HeadingDots from './HeadingDots';

const items = [
  {
    q: 'At what age can my child start using Spiick?',
    a: 'Spiick is designed for children as young as 4 years old in preschool, even if they can’t read yet. Learning happens through activities that emphasize listening, speaking and playing, in a natural and intuitive way.'
  },
  {
    q: 'My child can’t read yet. Is Spiick still suitable?',
    a: 'Yes, absolutely. Spiick is built around listening and speaking, not reading. Children learn just like they did with their native language.'
  },
  {
    q: 'How is Spiick different from an app?',
    a: 'Unlike apps, Spiick is a screen-free experience where children actively engage in our digital books and Spiick smart pen. They don’t just listen or tap — they interact, respond, and engage.'
  },
  {
    q: 'How much time should my child practice each day?',
    a: 'Our activities are designed to be short, engaging, and easy to fit into a daily routine. Just 10 to 20 minutes a day is enough to see real progress — consistency matters more than duration.'
  },
  {
    q: 'My child is shy. Will Spiick still work?',
    a: 'Yes. Spiick is especially effective for shy children. The pen creates a safe, pressure-free environment where they can express themselves confidently.'
  },
  {
    q: 'Does Spiick replace English classes?',
    a: 'Not necessarily. Spiick has a different goal: helping children acquire English naturally and become confident speakers, while traditional classes often focus on school lessons and grades. Spiick complements classroom learning by developing what is often missing: the ability to understand, speak, and interact in English with ease.'
  },
  {
    q: 'Will my child really start speaking English?',
    a: 'Yes. Speaking is at the heart of Spiick. From the very beginning, children are encouraged to listen, repeat, respond, and express themselves naturally in English.'
  },
  {
    q: 'Do I need an internet connection to use Spiick?',
    a: 'Yes and no. Many activities work offline once the content has been loaded onto the Spiick smart pen. However, features such as instant feedback, conversations, and pronunciation practice require an internet connection.'
  },
  {
    q: 'How does Spiick adapt to my child’s level?',
    a: 'Spiick is designed to adapt to your child’s age, cognitive development, and school level. Each child progresses at their own pace, with content that evolves step by step and adjusts to their strengths and difficulties.'
  },
  {
    q: 'What’s included in the Spiick program?',
    a: 'The Spiick program includes the interactive smart pen and a new digital book each month, as part of a complete English acquisition journey. Each book helps children speak through engaging, real-life situations while developing their listening skills, pronunciation, and confidence in English.'
  },
  {
    q: 'What is the difference between language learning and language acquisition? And why does it matter for young children?',
    a: 'Language learning is a conscious process: studying grammar rules, memorizing vocabulary, and learning how the language works. Language acquisition is natural and intuitive. It’s how children learn their first language — through listening, understanding, repetition, interaction, and emotional connection, until speaking becomes automatic. For young children, acquisition is far more effective. It helps them develop confidence, fluency, natural pronunciation, and the ability to speak without constantly thinking about rules. Spiick is built around language acquisition, helping children truly use English — not just study it.'
  }
];

export default function FAQ() {
  const [open, setOpen] = useState(-1);

  return (
    <section id="faq" className="relative w-full py-10 lg:py-14 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="text-center">
          <h2
            className="text-[52px] sm:text-[60px] lg:text-[68px] leading-[1.02] text-[#0c0a1a] tracking-tight"
            style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, letterSpacing: '-0.03em' }}
          >
            <span className="relative inline-block align-baseline whitespace-nowrap">
              <svg
                aria-hidden="true"
                viewBox="0 0 220 80"
                preserveAspectRatio="none"
                className="absolute -left-[4%] -right-[4%] top-[14%] -bottom-[10%] w-[108%] h-[96%] z-0 pointer-events-none"
              >
                <path
                  d="M9 32 C 30 22, 70 18, 112 16 C 150 14, 190 18, 213 26 C 218 38, 214 54, 208 64 C 175 72, 130 71, 92 70 C 60 70, 24 68, 7 60 C 2 50, 4 38, 9 32 Z"
                  fill="#abf1b2"
                  opacity="0.72"
                />
              </svg>
              <span className="relative z-10" style={{ color: '#004867' }}>Questions ?</span>
            </span>
          </h2>
        </div>

        <div className="mt-14 space-y-4 lg:space-y-5">
          {items.map((it, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="rounded-[36px] bg-[#F24540] overflow-hidden">
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-5 text-left px-6 sm:px-8 lg:px-10 h-[72px] lg:h-[80px] hover:bg-[#e23c37] transition-colors"
                  aria-expanded={isOpen}
                >
                  <span
                    className="text-[18px] sm:text-[19px] lg:text-[20px] text-white pr-4"
                    style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                  >
                    {it.q}
                  </span>
                  <span
                    aria-hidden="true"
                    className="shrink-0 w-10 h-10 lg:w-11 lg:h-11 rounded-full bg-[#F5B538] grid place-items-center text-white"
                  >
                    {isOpen ? <Minus className="w-5 h-5" strokeWidth={3} /> : <Plus className="w-5 h-5" strokeWidth={3} />}
                  </span>
                </button>

                <div className={`grid transition-all duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                  <div className="overflow-hidden bg-white">
                    <p
                      className="px-6 sm:px-8 lg:px-10 py-7 text-[15px] lg:text-[16px] leading-relaxed text-[#0c0a1a]"
                      style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400 }}
                    >
                      {it.a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
